import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './HomeHub.module.css';
import VeloxCard from '../card/VeloxCard';
import { useTheme } from '../../hooks/useTheme';
import { ThemeToggleIcon } from '../ThemeToggle';
import { useAccount } from '../../contexts/AccountContext';
import EntitlementsContext from '../../contexts/EntitlementsContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { SECTION_TABS } from '../../pages/admin/adminNavConfig';
import { getPinnedTeams, PINNED_TEAMS_CHANGED_EVENT } from '../../utils/enterprisePinnedTeams';
import Avatar from '../account/Avatar';
import AccountDialog from '../account/AccountDialog';
import accountStyles from '../account/Account.module.css';
import VeloxWordmark from '../brand/VeloxWordmark';
import { signOut } from '../../utils/logout';
import { isCompanyAdmin } from '../../utils/companyRoles';

/**
 * Every route that belongs to the account area, which is reached through the
 * sidebar's account button rather than a nav row. Listed once so the button's
 * active state and the nav's lack of one cannot disagree.
 */
function isAccountPath(pathname) {
  const p = pathname || '';
  return (
    p.startsWith('/account') ||
    p.startsWith('/settings') ||
    p.startsWith('/profile') ||
    p.startsWith('/notification-settings') ||
    p.startsWith('/privacy-settings') ||
    p.startsWith('/insight-preferences') ||
    p.startsWith('/delete-account') ||
    p.startsWith('/policy') ||
    // Plans, the configurator and billing are reached from the account area
    // rather than from primary navigation.
    p.startsWith('/upgrade') ||
    p.startsWith('/order') ||
    p.startsWith('/billing')
  );
}

function getActiveNavTo(pathname) {
  const p = pathname || '';
  if (p.startsWith('/enterprise-app/')) {
    const parts = p.split('/').filter(Boolean);
    if (parts.length >= 3) return `/${parts.slice(0, 3).join('/')}`;
  }
  if (p === '/home') return '/home';
  if (p.startsWith('/admin')) {
    const match = SECTION_TABS.find((tab) => p === tab.path || p.startsWith(`${tab.path}/`));
    return match?.path || '/admin/overview';
  }
  if (p.startsWith('/my-card')) return '/my-card';
  if (p.startsWith('/contacts')) return '/contacts';

  // Prefer exact Insights subroutes first
  if (p.startsWith('/insights/events') || p.startsWith('/events')) return '/insights/events';
  if (p.startsWith('/advanced-analytics')) return '/advanced-analytics';
  if (p.startsWith('/insights/analytics') || p.startsWith('/analytics')) return '/advanced-analytics';
  if (p.startsWith('/insights/news') || p.startsWith('/news') || p.startsWith('/insights'))
    return '/insights/news';

  if (p.startsWith('/archive')) return '/archive';

  // Account routes deliberately highlight nothing here — see isAccountPath.
  return null;
}

function SkeletonLine({ width = '100%', height = 12, radius = 999 }) {
  return (
    <span
      className={styles.skeleton}
      style={{
        width,
        height,
        borderRadius: radius,
        display: 'block',
      }}
      aria-hidden="true"
    />
  );
}

const VeloxIcon = ({ children, title }) => (
  <svg
    className={styles.icon}
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : 'true'}
    role={title ? 'img' : 'presentation'}
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

const IconMenu = () => (
  <VeloxIcon title="Menu">
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h16" />
  </VeloxIcon>
);

const IconBell = () => (
  <VeloxIcon title="Notifications">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </VeloxIcon>
);

const IconInfo = () => (
  <VeloxIcon title="Information">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </VeloxIcon>
);

const IconChevronRight = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const IconChevronDown = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const IconCheck = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.2 4.2L19 7.5" />
  </svg>
);

const IconSignOut = () => (
  <VeloxIcon title="Sign out">
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M10 8l-4 4 4 4" />
    <path d="M6 12h9" />
  </VeloxIcon>
);

const IconUsers = () => (
  <VeloxIcon title="Contacts">
    <path d="M17 21v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
    <circle cx="9" cy="9" r="3" />
    <path d="M23 21v-1a3.5 3.5 0 0 0-2.2-3.2" />
    <path d="M17 6.2a3 3 0 0 1 0 5.6" />
  </VeloxIcon>
);

const IconHome = () => (
  <VeloxIcon title="Home">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 10v10h14V10" />
  </VeloxIcon>
);

const IconCard = () => (
  <VeloxIcon title="My Card">
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <path d="M7 10h6" />
    <path d="M7 14h10" />
  </VeloxIcon>
);

const IconNews = () => (
  <VeloxIcon title="Market Intelligence">
    <path d="M4 6h14" />
    <path d="M4 10h14" />
    <path d="M4 14h10" />
    <path d="M20 6v12a2 2 0 0 1-2 2H6" />
  </VeloxIcon>
);

const IconCalendar = () => (
  <VeloxIcon title="Networking">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4" />
    <path d="M8 2v4" />
    <path d="M3 10h18" />
  </VeloxIcon>
);

const IconChart = () => (
  <VeloxIcon title="Analytics">
    <path d="M3 3v18h18" />
    <path d="M7 14v3" />
    <path d="M12 10v7" />
    <path d="M17 6v11" />
  </VeloxIcon>
);

const IconArchive = () => (
  <VeloxIcon title="Archive">
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
    <path d="M10 12h4" />
  </VeloxIcon>
);

const IconOrders = () => (
  <VeloxIcon title="Orders">
    <path d="M7 3h10v18l-2-1-2 1-2-1-2 1-2-1-2 1V3Z" />
    <path d="M9 7h6" />
    <path d="M9 11h6" />
  </VeloxIcon>
);

const IconDashboard = () => (
  <VeloxIcon title="Enterprise dashboard">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </VeloxIcon>
);

const IconSeats = () => (
  <VeloxIcon title="Seats">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </VeloxIcon>
);

const IconAccounts = () => (
  <VeloxIcon title="Account">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="3.6" />
  </VeloxIcon>
);

const IconContacts = () => (
  <VeloxIcon title="Company contacts">
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <circle cx="12" cy="10" r="2.4" />
    <path d="M8.5 17a3.5 3.5 0 0 1 7 0" />
  </VeloxIcon>
);

const IconBrand = () => (
  <VeloxIcon title="Branding">
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </VeloxIcon>
);

const IconInvite = () => (
  <VeloxIcon title="Invitations">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </VeloxIcon>
);


export function HomeTopBar({
  title,
  onMenu,
  notificationsBadge,
  onNotifications,
  onInfo,
  showThemeToggle,
  showNotifications = true,
}) {
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    document.title = `${title} | Velox Network`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    /* No account control here: the sidebar's account area is the single entry
       point, and repeating an avatar on every page's banner reads as clutter. */
    <header className={styles.topBar}>
      <div className={styles.topBarLeft}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onMenu}
          aria-label="Open navigation"
          aria-haspopup="dialog"
        >
          <IconMenu />
        </button>
        <div className={styles.pageTitle}>{title}</div>
      </div>

      <div className={styles.topBarRight}>
        {onInfo ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onInfo}
            aria-label="Information"
          >
            <IconInfo />
          </button>
        ) : null}
        {showThemeToggle ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={toggleTheme}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <ThemeToggleIcon isDark={isDarkMode} />
          </button>
        ) : null}
        {showNotifications ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onNotifications}
            aria-label="Open notifications"
          >
            <span className={styles.bellWrap}>
              <IconBell />
              {notificationsBadge?.type === 'count' && notificationsBadge.value > 0 ? (
                <span className={styles.badge} aria-label={`${notificationsBadge.value} notifications`}>
                  {notificationsBadge.value > 9 ? '9+' : notificationsBadge.value}
                </span>
              ) : notificationsBadge?.type === 'dot' ? (
                <span className={styles.badgeDot} aria-label="Notifications available" />
              ) : null}
            </span>
          </button>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Places in the app. Account and settings are deliberately absent: they live
 * in the sidebar's account area, which is the single entry point to them.
 */
const NAV_ITEMS_BASE = [
  { label: 'Home', to: '/home', icon: <IconHome /> },
  { label: 'Contacts', to: '/contacts', icon: <IconUsers /> },
  { label: 'My Card', to: '/my-card', icon: <IconCard /> },
  { label: 'Market Intelligence', to: '/insights/news', icon: <IconNews /> },
  { label: 'Networking', to: '/insights/events', icon: <IconCalendar /> },
  { label: 'Analytics', to: '/advanced-analytics', icon: <IconChart /> },
  { label: 'Archive', to: '/archive', icon: <IconArchive /> },
];

function getNavItems({ workspace, pinnedTeams = [], staffNavSections = null }) {
  if (workspace?.type === 'admin') {
    const tabs = staffNavSections
      ? SECTION_TABS.filter((tab) => staffNavSections.includes(tab.id))
      : SECTION_TABS;
    return [
      { kind: 'heading', label: 'Admin' },
      ...tabs.map((tab) => ({
        label: tab.label,
        to: tab.path,
        icon: tab.icon,
      })),
    ];
  }

  if (workspace?.type === 'company' && workspace.id) {
    const base = `/enterprise-app/${workspace.id}`;
    const isCompanyAdminUser = isCompanyAdmin({
      role: workspace.role,
      isOwner: workspace.isOwner,
      isAdmin: workspace.isAdmin,
    }) || !!workspace.isAdmin;
    const pins = [...pinnedTeams]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((pin) => ({
        kind: 'subitem',
        label: pin.name,
        to: `${base}/teams/${pin.teamId}`,
      }));

    return [
      { kind: 'heading', label: workspace.name || 'Company' },
      { label: 'Overview', to: `${base}/dashboard`, icon: <IconDashboard /> },
      { label: 'Teams', to: `${base}/teams`, icon: <IconUsers /> },
      ...pins,
      { label: 'Account', to: `${base}/members`, icon: <IconAccounts /> },
      { label: 'Seats', to: `${base}/seats`, icon: <IconSeats /> },
      { label: 'Branding', to: `${base}/branding`, icon: <IconBrand /> },
      ...(isCompanyAdminUser
        ? [
            { label: 'Company contacts', to: `${base}/contacts`, icon: <IconContacts /> },
            { label: 'Analytics', to: `${base}/analytics`, icon: <IconChart /> },
            { label: 'Orders', to: `${base}/orders`, icon: <IconOrders /> },
          ]
        : []),
      { label: 'Invitations', to: `${base}/invites`, icon: <IconInvite /> },
    ];
  }

  return [...NAV_ITEMS_BASE];
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Where the drawer gives way to the permanent rail. Must match the
 *  `min-width: 1024px` block in HomeHub.module.css. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * The sidebar's identity block: who you are, and the way in to your account.
 *
 * This replaced a static "Velox / Network" wordmark, which told the user
 * nothing they did not already know and left the app with no visible sign of
 * which account they were signed in to.
 */
function SidebarAccount({ onNavigate, isActive }) {
  const { account, avatarUrl, isLoading } = useAccount();
  const name = account?.name || '';
  const email = account?.email || '';

  return (
    <button
      type="button"
      className={`${styles.accountButton} ${isActive ? styles.accountButtonActive : ''}`}
      onClick={() => onNavigate('/account')}
      aria-current={isActive ? 'page' : undefined}
    >
      <Avatar name={name} src={avatarUrl} size={38} />
      <span className={styles.accountText}>
        {isLoading && !account ? (
          <>
            <span className={styles.accountSkeleton} style={{ width: '72%' }} aria-hidden="true" />
            <span className={styles.accountSkeleton} style={{ width: '52%', height: 9 }} aria-hidden="true" />
            <span className={styles.visuallyHidden}>Loading your account</span>
          </>
        ) : (
          <>
            <span className={styles.accountName}>{name || 'Your account'}</span>
            <span className={styles.accountMeta}>{email || 'View account'}</span>
          </>
        )}
      </span>
      <span className={styles.accountChevron} aria-hidden="true">
        <IconChevronRight />
      </span>
    </button>
  );
}

/** Not a workspace: the entry that leads someone with no company towards one. */
const TEAM_SETUP_VALUE = '__team_setup__';

function workspaceMark(item) {
  if (item.kind === 'setup') return '+';
  const letter = (item.label || '').trim().charAt(0);
  return letter ? letter.toUpperCase() : '·';
}

function WorkspaceSwitcher({ onNavigate }) {
  const { current, companies, selectPersonal, selectCompany, selectAdmin } = useWorkspace();
  const { account } = useAccount();
  const isPlatformAdmin = account?.role === 'admin' || account?.staff?.superAdmin || account?.staff?.status === 'active';
  const uid = useId();
  const listId = `${uid}-list`;
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // On a company URL the workspace is known before the list of companies has
  // loaded, so the current one is added to the options to keep the select in
  // step with what the sidebar is showing.
  const options = useMemo(() => {
    if (current.type !== 'company' || companies.some((c) => c.id === current.id)) return companies;
    return [...companies, { id: current.id, name: current.name }];
  }, [companies, current]);

  // With no company there is nothing to toggle between, so the control offers
  // Plans. While Teams is waitlist-only that entry joins the waitlist page;
  // once Teams is publicly available it offers workspace setup again.
  const entitlements = useContext(EntitlementsContext);
  const teamsOpen = entitlements?.launch?.teams === true;
  const offerTeamSetup = options.length === 0;
  const showAdmin = isPlatformAdmin || current.type === 'admin';

  const items = useMemo(() => {
    const list = [{ value: 'personal', label: 'Personal', kind: 'personal' }];
    if (showAdmin) list.push({ value: 'admin', label: 'Admin', kind: 'admin' });
    options.forEach((c) => list.push({ value: c.id, label: c.name, kind: 'company' }));
    if (offerTeamSetup) {
      list.push({
        value: TEAM_SETUP_VALUE,
        label: teamsOpen ? 'Set up a team workspace…' : 'Add a Team workspace',
        kind: 'setup',
      });
    }
    return list;
  }, [offerTeamSetup, options, showAdmin, teamsOpen]);

  const value =
    current.type === 'company' ? current.id : current.type === 'admin' ? 'admin' : 'personal';
  const selected = items.find((item) => item.value === value) || items[0];
  const selectedIndex = Math.max(
    0,
    items.findIndex((item) => item.value === value)
  );

  const chooseWorkspace = useCallback(
    (next) => {
      setOpen(false);
      if (next === value && next !== TEAM_SETUP_VALUE) return;
      if (next === TEAM_SETUP_VALUE) {
        // A workspace needs Teams, so Plans should feature that card.
        onNavigate('/upgrade?focus=teams');
        return;
      }
      if (next === 'personal') {
        selectPersonal();
        onNavigate('/home');
        return;
      }
      if (next === 'admin') {
        selectAdmin();
        onNavigate('/admin/overview');
        return;
      }
      const company = options.find((c) => c.id === next);
      selectCompany(next, company);
      onNavigate(`/enterprise-app/${next}/dashboard`);
    },
    [onNavigate, options, selectAdmin, selectCompany, selectPersonal, value]
  );

  const moveHighlight = useCallback(
    (delta) => {
      setHighlight((currentHighlight) => {
        const count = items.length;
        if (count === 0) return 0;
        return (currentHighlight + delta + count) % count;
      });
    },
    [items.length]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setHighlight(selectedIndex);
        setOpen(true);
        return;
      }
      moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (!open) return;
    if (event.key === 'Home') {
      event.preventDefault();
      setHighlight(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setHighlight(items.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const item = items[highlight];
      if (item) chooseWorkspace(item.value);
    }
  };

  const highlighted = items[highlight];

  return (
    <div className={styles.workspaceSwitch} ref={rootRef}>
      <span className={styles.workspaceSwitchLabel} id={`${uid}-label`}>
        Workspace
      </span>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        className={`${styles.workspaceSelect} ${open ? styles.workspaceSelectOpen : ''}`}
        aria-label="Switch workspace"
        aria-autocomplete="none"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && highlighted ? `${uid}-opt-${highlighted.value}` : undefined}
        onClick={() => {
          if (!open) setHighlight(selectedIndex);
          setOpen((isOpen) => !isOpen);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.workspaceMark} aria-hidden="true">
          {workspaceMark(selected)}
        </span>
        <span className={styles.workspaceSelectValue}>{selected.label}</span>
        <span className={styles.workspaceSelectChevron} aria-hidden="true">
          <IconChevronDown />
        </span>
      </button>
      <ul
        id={listId}
        className={styles.workspaceMenu}
        role="listbox"
        aria-label="Workspaces"
        hidden={!open}
      >
        {items.map((item, index) => {
          const isSelected = item.value === value;
          const isActive = index === highlight;
          return (
            <li
              key={item.value}
              id={`${uid}-opt-${item.value}`}
              role="option"
              aria-selected={isSelected}
              className={[
                styles.workspaceOption,
                isSelected ? styles.workspaceOptionSelected : '',
                isActive ? styles.workspaceOptionActive : '',
                item.kind === 'setup' ? styles.workspaceOptionAction : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => chooseWorkspace(item.value)}
            >
              <span className={styles.workspaceMark} aria-hidden="true">
                {workspaceMark(item)}
              </span>
              <span className={styles.workspaceOptionLabel}>{item.label}</span>
              {isSelected ? (
                <span className={styles.workspaceOptionCheck} aria-hidden="true">
                  <IconCheck />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The sidebar's contents, rendered once into the mobile drawer and once into
 * the desktop rail. Previously this markup existed twice in this file, so the
 * nav list and the header had to be edited in two places to stay in step.
 */
function SidebarBody({
  navItems,
  activeTo,
  activePath,
  accountActive,
  onNavigate,
  onSignOut,
  footerExtra,
}) {
  return (
    <>
      <div className={styles.sidebarHeader}>
        <div className={styles.brand}>
          <VeloxWordmark size={14} />
        </div>
        <SidebarAccount onNavigate={onNavigate} isActive={accountActive} />
        <WorkspaceSwitcher onNavigate={onNavigate} />
      </div>

      <nav className={styles.nav} aria-label="Primary">
        {navItems.map((item) => {
          if (item.kind === 'heading') {
            return (
              <p key={`heading-${item.label}`} className={styles.navHeading}>
                {item.label}
              </p>
            );
          }
          // A pinned team is a specific route, so it matches the full path
          // rather than the section that getActiveNavTo collapses it to.
          const isActive =
            item.kind === 'subitem' ? activePath === item.to : activeTo === item.to;
          return (
            <button
              key={item.to}
              type="button"
              className={[
                styles.navItem,
                item.kind === 'subitem' ? styles.navSubItem : '',
                isActive ? styles.navItemActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onNavigate(item.to)}
              aria-current={isActive ? 'page' : undefined}
              title={item.kind === 'subitem' ? item.label : undefined}
            >
              {item.icon ? (
                <span className={styles.navIcon} aria-hidden="true">
                  {item.icon}
                </span>
              ) : null}
              <span className={styles.navLabel}>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className={styles.sidebarFooter}>
        {footerExtra}
        <button type="button" className={styles.signOutButton} onClick={onSignOut}>
          <span className={styles.navIcon} aria-hidden="true">
            <IconSignOut />
          </span>
          <span className={styles.navLabel}>Sign out</span>
        </button>
      </div>
    </>
  );
}

export function HomeSidebar({ open, onClose, activePath, onNavigate, footerExtra }) {
  const navigate = useNavigate();
  const activeTo = getActiveNavTo(activePath);
  const accountActive = isAccountPath(activePath);
  const { current } = useWorkspace();
  const { account, clearAccount } = useAccount();

  // Teams pinned on the Teams page appear under it, on every page of the
  // company workspace. They live in localStorage and change without a route
  // change, hence the event subscription.
  const companyId = current.type === 'company' ? current.id : null;
  const [pinnedTeams, setPinnedTeamsState] = useState(() =>
    companyId ? getPinnedTeams(companyId) : []
  );

  useEffect(() => {
    if (!companyId) {
      setPinnedTeamsState([]);
      return undefined;
    }
    setPinnedTeamsState(getPinnedTeams(companyId));
    const handler = () => setPinnedTeamsState(getPinnedTeams(companyId));
    window.addEventListener(PINNED_TEAMS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(PINNED_TEAMS_CHANGED_EVENT, handler);
  }, [companyId]);

  const navItems = getNavItems({
    workspace: current,
    pinnedTeams,
    staffNavSections: account?.staff?.navSections || null,
  });

  const [signOutOpen, setSignOutOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const drawerRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Crossing into the desktop breakpoint swaps the drawer for the permanent
  // rail. Without this the drawer's open state would survive the swap and the
  // scroll lock would outlive the thing that set it.
  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    if (mq.matches) {
      onCloseRef.current?.();
      return undefined;
    }
    const onChange = (e) => {
      if (e.matches) onCloseRef.current?.();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open]);

  // Escape to close, and Tab kept inside the panel while it is open.
  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    const raf = requestAnimationFrame(() => {
      const first = drawerRef.current?.querySelector(FOCUSABLE);
      first?.focus?.({ preventScroll: true });
    });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;

      const items = Array.from(drawerRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      const restore = restoreFocusRef.current;
      if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
        restore.focus({ preventScroll: true });
      }
    };
  }, [open]);

  // Hub pages scroll inside .hubRoot rather than the body, so the lock is a
  // document-level flag that HomeHub.module.css keys off — see the
  // html[data-vx-drawer='open'] rules there.
  useEffect(() => {
    if (!open) return undefined;
    document.documentElement.setAttribute('data-vx-drawer', 'open');
    return () => document.documentElement.removeAttribute('data-vx-drawer');
  }, [open]);

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      clearAccount();
      setIsSigningOut(false);
      setSignOutOpen(false);
      onCloseRef.current?.();
      navigate('/login', { replace: true });
    }
  }, [clearAccount, navigate]);

  const body = (
    <SidebarBody
      navItems={navItems}
      activeTo={activeTo}
      activePath={activePath}
      accountActive={accountActive}
      onNavigate={onNavigate}
      onSignOut={() => setSignOutOpen(true)}
      footerExtra={footerExtra}
    />
  );

  return (
    <>
      <div
        className={`${styles.drawerOverlay} ${open ? styles.drawerOverlayOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Mobile drawer (< 1024px) */}
      <aside
        ref={drawerRef}
        className={`${styles.sidebar} ${open ? styles.sidebarOpen : ''}`}
        role="dialog"
        /* Only a modal while it is actually over the page. */
        aria-modal={open ? 'true' : undefined}
        aria-label="Navigation"
        aria-hidden={!open}
      >
        {body}
      </aside>

      {/* Desktop rail (always visible at >= 1024px) */}
      <aside className={styles.sidebarDesktop} aria-label="Sidebar">
        {body}
      </aside>

      <AccountDialog
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        title="Sign out of Velox?"
        lead="You will need your email and password to sign back in on this device."
        footer={
          <>
            <button
              type="button"
              className={accountStyles.secondaryButton}
              onClick={() => setSignOutOpen(false)}
              disabled={isSigningOut}
            >
              Stay signed in
            </button>
            <button
              type="button"
              className={accountStyles.primaryButton}
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing out…' : 'Sign out'}
            </button>
          </>
        }
      />
    </>
  );
}

export function KpiCard({ label, value, helper, emptyLabel, to, ariaLabel }) {
  const hasValue = typeof value === 'number' && Number.isFinite(value);
  const isLoading = value === '__loading__';
  const interactive = Boolean(to) && !isLoading;
  const rootClass = `${styles.kpiCard}${interactive ? ` ${styles.kpiCardInteractive}` : ''}`;

  const body = (
    <>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>
        {isLoading ? (
          <div className={styles.kpiSkeletonWrap}>
            <SkeletonLine width="56%" height={26} radius={10} />
          </div>
        ) : hasValue ? (
          <span className={styles.goldNumber}>{value}</span>
        ) : (
          <span className={styles.kpiEmpty}>{emptyLabel || '—'}</span>
        )}
      </div>
      {isLoading ? (
        <div className={styles.kpiHelper}>
          <SkeletonLine width="72%" height={12} radius={8} />
        </div>
      ) : helper ? (
        <div className={styles.kpiHelper}>{helper}</div>
      ) : null}
    </>
  );

  if (interactive) {
    return (
      <Link
        to={to}
        className={rootClass}
        aria-label={ariaLabel || `${label}: view details`}
      >
        {body}
      </Link>
    );
  }

  return <div className={rootClass}>{body}</div>;
}

export function ModuleCard({ title, subtitle, actionLabel, actionDisabled, onAction, children, className }) {
  return (
    <section className={[styles.moduleCard, className].filter(Boolean).join(' ')} aria-label={title}>
      <div className={styles.moduleHeader}>
        <div className={styles.moduleHeaderText}>
          <div className={styles.moduleTitle}>{title}</div>
          {subtitle ? <div className={styles.moduleSubtitle}>{subtitle}</div> : null}
        </div>
        {actionLabel ? (
          <button
            type="button"
            className={styles.moduleAction}
            onClick={onAction}
            disabled={actionDisabled}
            aria-disabled={actionDisabled ? 'true' : undefined}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
      <div className={styles.moduleBody}>{children}</div>
    </section>
  );
}

export function ModuleSkeletonList({ rows = 2 }) {
  return (
    <div className={styles.skeletonList} aria-hidden="true">
      {Array.from({ length: rows }).map((_, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className={styles.skeletonListRow}>
          <SkeletonLine width="82%" height={12} radius={10} />
          <SkeletonLine width="56%" height={10} radius={10} />
        </div>
      ))}
    </div>
  );
}

export function ModuleSkeletonNews() {
  return (
    <div className={styles.skeletonNews} aria-hidden="true">
      <div className={styles.skeletonPillRow}>
        <SkeletonLine width={92} height={18} radius={999} />
        <SkeletonLine width={88} height={12} radius={10} />
      </div>
      <div className={styles.skeletonTextBlock}>
        <SkeletonLine width="92%" height={14} radius={10} />
        <SkeletonLine width="76%" height={14} radius={10} />
        <SkeletonLine width="88%" height={12} radius={10} />
        <SkeletonLine width="64%" height={12} radius={10} />
      </div>
    </div>
  );
}

export function EmptyState({ title, body, ctaLabel, onCta }) {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyTitle}>{title}</div>
      {body ? <div className={styles.emptyBody}>{body}</div> : null}
      {ctaLabel ? (
        <button type="button" className={styles.primaryButton} onClick={onCta}>
          {ctaLabel}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The most recent contact, shown as the real Velox card with the next two
 * contacts suggested as shells behind it.
 *
 * The card itself is in normal flow and the shells are absolutely positioned
 * against it, so the stack's height is whatever the card needs — no magic
 * height to keep in sync with the card's content.
 */
export function LastSavedCardStack({ contacts, onOpenContacts }) {
  const stack = Array.isArray(contacts) ? contacts.slice(0, 3) : [];
  const [top] = stack;
  if (!top) return null;

  return (
    <div className={styles.cardStackWrap}>
      <button
        type="button"
        className={styles.cardStackHit}
        onClick={onOpenContacts}
        aria-label={`${top.name || 'Most recent contact'} — open Contacts`}
      >
        <div className={styles.cardStack}>
          {stack.length > 2 ? <span className={styles.stackShell2} aria-hidden="true" /> : null}
          {stack.length > 1 ? <span className={styles.stackShell1} aria-hidden="true" /> : null}
          <VeloxCard
            className={styles.stackTop}
            card={top}
            footer={
              <span className={styles.stackCtaRow}>
                <span className={styles.stackHint}>Open Contacts</span>
                <span className={styles.stackCtaArrow} aria-hidden="true">
                  →
                </span>
              </span>
            }
          />
        </div>
      </button>
    </div>
  );
}

/** Matches the card stack's footprint so Home does not reflow when it resolves. */
export function CardStackSkeleton() {
  return (
    <div className={styles.cardStackWrap} aria-hidden="true">
      <div className={styles.cardStack}>
        <span className={styles.stackShell2} />
        <span className={styles.stackShell1} />
        <div className={`${styles.stackTop} ${styles.stackSkeleton}`}>
          <SkeletonLine width="54%" height={18} radius={10} />
          <SkeletonLine width="72%" height={12} radius={8} />
          <span className={styles.stackSkeletonRule} />
          <SkeletonLine width="64%" height={12} radius={8} />
          <SkeletonLine width="46%" height={12} radius={8} />
        </div>
      </div>
    </div>
  );
}

